import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { Dialog } from '@/components/ui/Dialog';
import { clearOldEvents } from '@/api/endpoints/dashboard';
import { errorMessage } from '@/api/errors';

const CONFIRMATION = 'DELETE';

/**
 * Retention cleanup.
 *
 * Crash records can be evidence in an insurance claim or a prosecution, so deleting
 * them is deliberately awkward: administrator-only, audit-logged server-side, and
 * bounded to records past the 365-day retention window. The typed confirmation is
 * there so this is never a misplaced click.
 */
export function MaintenancePage() {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const cleanup = useMutation({
    mutationFn: clearOldEvents,
    onSuccess: async (result) => {
      setConfirmOpen(false);
      setTyped('');
      await queryClient.invalidateQueries();
      toast.success(
        result.deleted_count === 0
          ? 'Nothing was past the retention window.'
          : `${result.deleted_count} crash record${result.deleted_count === 1 ? '' : 's'} deleted.`
      );
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader title="Maintenance" description="Data retention and housekeeping." />

      <Card className="max-w-2xl">
        <CardHeader
          title="Crash record retention"
          description="Remove crash events past the 365-day retention window."
        />
        <CardBody className="space-y-4">
          <div className="space-y-2 text-[13px] text-ink-secondary">
            <p>
              This permanently deletes crash events older than the configured retention period,
              together with their alert delivery records.
            </p>
            <p>
              <span className="font-medium text-ink">Crash records may be legally significant.</span>{' '}
              They can be evidence in an insurance claim or a prosecution. Every deletion is recorded
              in the server audit log against your account.
            </p>
          </div>

          <Button variant="danger" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="size-4" aria-hidden />
            Delete records past retention
          </Button>
        </CardBody>
      </Card>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setTyped('');
        }}
        title="Delete crash records past retention?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={typed !== CONFIRMATION}
              loading={cleanup.isPending}
              onClick={() => cleanup.mutate()}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-[13px] text-ink-secondary">
            This cannot be undone, and the deleted records cannot be reconstructed. The action is
            logged against your account.
          </p>
          <TextField
            label={`Type ${CONFIRMATION} to confirm`}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
          />
        </div>
      </Dialog>
    </>
  );
}
